import type React from "react";
import { UserCheck } from "lucide-react";
import { Button } from "@planta-v2/pds-ui/button";
import { UserAppRoutes } from "@shared/constants/user-app-routes.constants";
import { i18n } from "@frontend/i18n";

const TERMS_SENTINEL = "\x00TERMS\x00";
const PRIVACY_SENTINEL = "\x00PRIVACY\x00";

interface FutureEventsSectionProps {
  readonly participantIsolated: boolean;
  readonly isOptingIn: boolean;
  readonly onOptIn: () => void;
  readonly titleOverride?: string;
  readonly buttonLabelOverride?: string;
  readonly disclaimerOverride?: React.ReactNode;
  readonly iconOverride?: React.ReactNode;
  readonly testIdPrefix?: string;
}

function LegalDescription({
  testIdPrefix = "future-events",
}: {
  readonly testIdPrefix?: string;
}): React.JSX.Element {
  const termsHref = UserAppRoutes.terms();
  const privacyHref = UserAppRoutes.privacy();

  const raw = i18n.t("user.profile.preferences.futureEvents.description", {
    terms: TERMS_SENTINEL,
    privacy: PRIVACY_SENTINEL,
  });

  const parts = raw
    .split(TERMS_SENTINEL)
    .flatMap((chunk, i) =>
      i === 0
        ? [chunk]
        : [
            <a
              key="terms"
              data-testid={`${testIdPrefix}-terms-link`}
              href={termsHref}
              target="_blank"
              rel="noopener"
              className="underline"
            >
              {i18n.t("user.profile.preferences.futureEvents.termsLabel")}
            </a>,
            chunk,
          ],
    )
    .flatMap((node) => {
      if (typeof node !== "string") return [node];
      const segs = node.split(PRIVACY_SENTINEL);
      if (segs.length === 1) return [node];
      return segs.flatMap((seg, i) =>
        i === 0
          ? [seg]
          : [
              <a
                key="privacy"
                data-testid={`${testIdPrefix}-privacy-link`}
                href={privacyHref}
                target="_blank"
                rel="noopener"
                className="underline"
              >
                {i18n.t("user.profile.preferences.futureEvents.privacyLabel")}
              </a>,
              seg,
            ],
      );
    });

  return <p className="text-sm text-muted-foreground">{parts}</p>;
}

export function FutureEventsSection({
  participantIsolated,
  isOptingIn,
  onOptIn,
  titleOverride,
  buttonLabelOverride,
  disclaimerOverride,
  iconOverride,
  testIdPrefix = "future-events",
}: FutureEventsSectionProps): React.JSX.Element {
  const isRemembered = !participantIsolated;
  const title =
    titleOverride ?? i18n.t("user.profile.preferences.futureEvents.title");
  const notRememberedLabel =
    buttonLabelOverride ??
    i18n.t("user.profile.preferences.futureEvents.buttonRemember");
  const icon = iconOverride ?? <UserCheck className="mr-2 h-4 w-4" />;

  return (
    <div
      data-testid={`${testIdPrefix}-section`}
      className="flex flex-col gap-2"
    >
      <p className="text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground">
        {title}
      </p>
      <div className="flex flex-col gap-4 rounded-md border border-foreground/15 bg-card p-4">
        <Button
          data-testid={`${testIdPrefix}-remember-button`}
          className="w-full transform-gpu"
          disabled={isRemembered || isOptingIn}
          onClick={onOptIn}
        >
          {icon}
          {isRemembered
            ? i18n.t("user.profile.preferences.futureEvents.buttonRemembered")
            : notRememberedLabel}
        </Button>
        {disclaimerOverride ?? <LegalDescription testIdPrefix={testIdPrefix} />}
      </div>
    </div>
  );
}
